import { DateTime } from "luxon";
import { z } from "zod";

import { normalizeSimpleDate } from "@/src/lib/utils/date-and-phone-utils";
import { isoDateTimeSchema, simpleDateSchema } from "@/src/models/common-schema";

export enum SupplementalAnswer {
  YES = "Yes",
  NO = "No",
  UNKNOWN = "Unknown",
}

const supplementalAnswerSchema = z.nativeEnum(SupplementalAnswer);

// Legacy checklist values and review-UI labels. Rewording breaks old packets.
export const PATIENT_SUPPLEMENTAL_FIELDS = {
  resistantOrganisms: "Resistant organisms",
  covid19: "COVID-19",
  hbv: "HBV",
  tuberculosis: "Tuberculosis",
  ivMedication: "IV medication",
  lifevest: "LifeVest",
  lvad: "LVAD",
  tracheostomyCapped: "Tracheostomy - capped",
  bipapOrTracheostomyUncapped: "BiPap/Tracheostomy - uncapped",
  hoyerLift: "Hoyer lift",
  dialysisBedOrStretcher: "Dialysis bed or stretcher",
  cognitiveBehavioralAssistance: "Extra assistance during treatment for cognitive/behavioral issues",
  pediatricPatient: "Pediatric patient",
  bariatricChair: "Bariatric chair",
} as const;

export type PatientSupplementalFieldKey = keyof typeof PATIENT_SUPPLEMENTAL_FIELDS;

export const PATIENT_SUPPLEMENTAL_FIELD_KEYS = Object.keys(
  PATIENT_SUPPLEMENTAL_FIELDS
) as PatientSupplementalFieldKey[];

export const patientSupplementalAnswersSchema = z.object(
  Object.fromEntries(PATIENT_SUPPLEMENTAL_FIELD_KEYS.map((key) => [key, supplementalAnswerSchema.nullable()])) as {
    [K in PatientSupplementalFieldKey]: z.ZodNullable<typeof supplementalAnswerSchema>;
  }
);

export type PatientSupplementalAnswers = z.infer<typeof patientSupplementalAnswersSchema>;

export const patientSupplementalAnswersShape: PatientSupplementalAnswers = Object.fromEntries(
  PATIENT_SUPPLEMENTAL_FIELD_KEYS.map((key) => [key, SupplementalAnswer.UNKNOWN])
) as PatientSupplementalAnswers;

const CHAIR_TIME_FORMATS = ["h:mm a", "h:mma", "H:mm", "HH:mm"] as const;

export function toPreferredChairOffsetDateTime(
  chairTime: string,
  treatmentStartDate: string | null | undefined,
  timezone: string
): string | null {
  const trimmed = chairTime.trim();

  if (!trimmed) return null;

  if (isoDateTimeSchema.safeParse(trimmed).success) {
    return DateTime.fromISO(trimmed, { setZone: true }).toUTC().toISO();
  }

  const clock = CHAIR_TIME_FORMATS.map((format) => DateTime.fromFormat(trimmed, format, { locale: "en-US" })).find(
    (parsed) => parsed.isValid
  );

  if (!clock) return null;

  const day = normalizeSimpleDate(treatmentStartDate);

  if (!day || !timezone.trim()) return null;

  return DateTime.fromISO(day, { zone: timezone })
    .set({ hour: clock.hour, minute: clock.minute, second: 0, millisecond: 0 })
    .toUTC()
    .toISO();
}

export const preferredChairTimeSchema = z
  .string()
  .nullable()
  .transform((value) => {
    if (value == null) return null;
    const trimmed = value.trim();

    return trimmed === "" ? null : trimmed;
  })
  .describe("Preferred chair time as a clock time (e.g. 8:00 AM) or an ISO datetime with offset.");

// ValerieHealthRegistrationDto has no slot for these, so they are extracted but never posted.
export const patientSupplementalReviewOnlySchema = z.object({
  firstDialysisDate: simpleDateSchema
    .nullable()
    .describe("First-ever dialysis date (FDOD) when documented, distinct from the intended start date."),
});

// Registration clinicals posted on ValerieHealthRegistrationDto (not the questionnaire).
// `treatmentStartDate` is intentionally distinct from prior-authorization `startDate`.
export const patientSupplementalRegistrationSchema = z.object({
  treatmentStartDate: simpleDateSchema
    .nullable()
    .describe("Treatment or registration start date when documented (Intake Manager StartDate)."),
  admitTypeId: z.number().int().nullable().describe("Intake Manager AdmitTypeId when documented as an id."),
  facilityId: z.number().int().nullable().describe("Intake Manager FacilityId when documented as an id."),
  diagnosisId: z.number().int().nullable().describe("Intake Manager DiagnosisId when documented as an id."),
  modalityId: z.number().int().nullable().describe("Intake Manager ModalityId when documented as an id."),
  preferredChairSchedule: z
    .string()
    .nullable()
    .describe("Preferred chair schedule text (e.g. MWF, TTS) when documented."),
  preferredChairTime: preferredChairTimeSchema,
  referralId: z.number().int().nullable().describe("Intake Manager ReferralId when documented as an id."),
  referringPhysicianId: z
    .number()
    .int()
    .nullable()
    .describe("Intake Manager ReferringPhysicianId when documented as an id."),
});

export type PatientSupplementalRegistration = z.infer<typeof patientSupplementalRegistrationSchema>;

export const PATIENT_SUPPLEMENTAL_REGISTRATION_FIELD_KEYS = Object.keys(
  patientSupplementalRegistrationSchema.shape
) as (keyof PatientSupplementalRegistration)[];

export const patientSupplementalBlockFieldsSchema = patientSupplementalAnswersSchema
  .merge(patientSupplementalRegistrationSchema)
  .merge(patientSupplementalReviewOnlySchema);
